import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import { otpService } from '../../../services/otpService';

const OTPVerificationModal = ({ 
  isOpen, 
  onClose, 
  onVerify, 
  email, 
  mobile,
  isLoading,
  error: externalError 
}) => {
  const [verificationType, setVerificationType] = useState('email');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Track previous external error to detect new errors
  const [prevExternalError, setPrevExternalError] = useState('');
  
  // Sync external error to internal state (only for new errors, not when user is typing)
  useEffect(() => {
    if (externalError && externalError !== prevExternalError) {
      setPrevExternalError(externalError);
      setError(externalError);
    } else if (!externalError && prevExternalError) {
      setPrevExternalError('');
      setError('');
    }
  }, [externalError, prevExternalError]);
  
  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setVerificationType('email');
      setError('');
      setOtp(['', '', '', '', '', '']);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, verificationType]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/?.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value?.slice(-1);
    setOtp(newOtp);
    
    setError('');
    setSuccessMessage('');

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !otp?.[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 6);
    if (!/^\d+$/?.test(pastedData)) return;

    const newOtp = pastedData?.split('')?.concat(Array(6)?.fill(''))?.slice(0, 6);
    setOtp(newOtp);
    setError('');
    setSuccessMessage('');
  };

  const handleVerify = async () => {
    const otpValue = otp?.join('');
    if (otpValue?.length !== 6) {
      setError('Please enter complete 6-digit OTP');
      return;
    }

    setError('');
    setSuccessMessage('');
    
    try {
      await onVerify(otpValue, verificationType);
      setError('');
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      const errorMessage = err?.message || 'Verification failed. Please try again.';
      setError(errorMessage);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError('');
    setSuccessMessage('');
    setOtp(['', '', '', '', '', '']);

    try {
      if (verificationType === 'email') {
        await otpService?.sendEmailOTP(email, 'User');
        setSuccessMessage('New OTP sent to your email! Please check your inbox.');
      } else {
        await otpService?.sendMobileOTP(mobile, 'User');
        setSuccessMessage('New OTP sent via SMS to your mobile number!');
      }
      
      setResendTimer(60);
      setCanResend(false);
    } catch (err) {
      const errorMsg = err?.message || 'Failed to resend OTP. Please try again.';
      setError(errorMsg);
    } finally {
      setResendLoading(false);
    }
  };

  const switchVerificationType = async (type) => {
    if (isLoading || resendLoading || type === verificationType) return;
    
    setVerificationType(type);
    setOtp(['', '', '', '', '', '']);
    setError('');
    setSuccessMessage('');
    setResendTimer(60);
    setCanResend(false);
    setResendLoading(true);

    try {
      if (type === 'email') {
        await otpService?.sendEmailOTP(email, 'User');
        setSuccessMessage('OTP sent to your email!');
      } else {
        await otpService?.sendMobileOTP(mobile, 'User');
        setSuccessMessage('OTP sent via SMS to your mobile number!');
      }
    } catch (err) {
      const errorMsg = err?.message || 'Failed to send OTP. Please try again.';
      setError(errorMsg);
    } finally {
      setResendLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1030 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card rounded-xl shadow-2xl border border-border overflow-hidden">
        <div className="bg-primary/5 px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="ShieldCheck" size={24} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Verify Your Account</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
            disabled={isLoading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Verification type selector - both options available */}
          <div className="flex gap-2 p-1 bg-muted rounded-lg">
            <button
              onClick={() => switchVerificationType('email')}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-smooth ${
                verificationType === 'email' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Mail" size={18} />
              <span className="font-medium">Email</span>
            </button>
            <button
              onClick={() => switchVerificationType('mobile')}
              disabled={isLoading || !mobile}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-smooth ${
                verificationType === 'mobile' ?'bg-primary text-primary-foreground shadow-sm'
                  : !mobile
                  ? 'text-muted-foreground opacity-50 cursor-not-allowed'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title={!mobile ? 'No mobile number provided' : 'Verify via SMS'}
            >
              <Icon name="Smartphone" size={18} />
              <span className="font-medium">SMS</span>
            </button>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              {verificationType === 'email' ? "We've sent a 6-digit code to your email" :"We've sent a 6-digit code via SMS to"}
            </p>
            <p className="font-medium text-foreground">
              {verificationType === 'email' ? email : mobile}
            </p>
            {verificationType === 'mobile' && (
              <div className="mt-2 p-2 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-xs text-primary font-medium flex items-center justify-center gap-1">
                  <Icon name="MessageSquare" size={14} />
                  <span>Real SMS sent via Twilio to your mobile number</span>
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Enter OTP
            </label>
            <div className="flex gap-2 justify-center" onPaste={handlePaste}>
              {otp?.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e?.target?.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isLoading}
                  className="w-12 h-12 md:w-14 md:h-14 text-center text-xl font-semibold bg-background border-2 border-input rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-smooth disabled:opacity-50"
                />
              ))}
            </div>
            {error && (
              <div className="mt-3 p-3 bg-error/10 border border-error/20 rounded-lg">
                <p className="text-sm text-error flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} />
                  <span>{error}</span>
                </p>
              </div>
            )}
            {successMessage && (
              <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-lg">
                <p className="text-sm text-success flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} />
                  <span>{successMessage}</span>
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {canResend ? (
                "Didn't receive code?"
              ) : (
                `Resend in ${resendTimer}s`
              )}
            </span>
            <button
              onClick={handleResend}
              disabled={!canResend || isLoading || resendLoading}
              className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1"
            >
              {resendLoading ? (
                <>
                  <Icon name="Loader2" size={14} className="animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend OTP'
              )}
            </button>
          </div>

          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={handleVerify}
            disabled={isLoading || otp?.join('')?.length !== 6}
            loading={isLoading}
            iconName="CheckCircle"
            iconPosition="right"
          >
            {isLoading ? 'Verifying...' : 'Verify & Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationModal;